import {
  compareMenu,
  DishInputListSchema,
  parseMenuText,
  parsePrice,
  PriceSchema,
  startingDish,
  type DishInput,
  type MenuReadItem,
  type MenuReading,
  type MenuRow,
} from '@antoja/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { t } from '../i18n';
import { Icon, type IconName } from '../icons';
import type { ConsoleApi, MenuDetail, MenuDish } from '../lib/api';
import { formatDate, formatPrice } from '../places/format';
import { hrefFor } from '../shell/route';
import { buttonBase, controlClass, secondaryLook } from '../styles';
import { Button, Card, Field, FormFooter, Notice, PageHeader } from '../ui';
import { errorText } from '../lib/errors';
import { menuLabel } from './format';

/** One row of the editor's draft: what Antoja will show for a dish. */
interface DraftDish {
  readonly key: string;
  readonly section: string | null;
  readonly name: string;
  readonly price: string;
  /** Null for a dish the editor added by hand. */
  readonly row: MenuRow<MenuDish> | null;
}

/** Readings that can be compared: an unreadable one counts as missing. */
const itemsOf = (reading: MenuReading | null) => (reading?.readable ? reading.items : null);

function buildDraft(menu: MenuDetail): DraftDish[] {
  const rows = compareMenu(itemsOf(menu.first), itemsOf(menu.second), menu.dishes);
  return rows.map((row) => {
    const start = startingDish(row, menu.dishes.length > 0);
    return {
      key: row.key,
      section: row.dish?.section ?? row.first?.section ?? row.second?.section ?? null,
      name: start.name,
      price: start.price === null ? '' : String(start.price),
      row,
    };
  });
}

const priceIsValid = (text: string) => PriceSchema.nullable().safeParse(parsePrice(text)).success;

/** A row the readings disagree on, already settled by a saved dish. */
const isReviewed = (row: DraftDish['row']) => Boolean(row?.difference && row.dish);

function comparisonOf(
  row: DraftDish['row'],
  compared: boolean,
): { icon: IconName; label: string; differs: boolean } {
  if (!row) return { icon: 'plus', label: t('menu.compare.added'), differs: false };
  if (!row.first && !row.second) {
    return { icon: 'none', label: t('menu.compare.onlyAntoja'), differs: false };
  }
  if (!compared) return { icon: 'pending', label: t('menu.compare.pending'), differs: false };
  if (isReviewed(row)) return { icon: 'check', label: t('menu.compare.reviewed'), differs: false };
  if (row.difference === 'missing') {
    return { icon: 'oneOfTwo', label: t('menu.compare.missing'), differs: true };
  }
  if (row.difference) {
    return { icon: 'conflict', label: t(`menu.compare.${row.difference}`), differs: true };
  }
  return { icon: 'check', label: t('menu.compare.same'), differs: false };
}

export function MenuScreen({
  api,
  placeId,
  menuId,
  canEdit,
}: {
  api: ConsoleApi;
  placeId: string;
  menuId: string;
  canEdit: boolean;
}) {
  const menu = useQuery({ queryKey: ['menu', menuId], queryFn: () => api.getMenu(menuId) });
  const found = menu.data?.placeId === placeId ? menu.data : null;
  return (
    <>
      <a
        href={hrefFor({ name: 'place', id: placeId })}
        className="-mb-3 -ml-2 inline-flex min-h-11 items-center gap-1.5 justify-self-start rounded-lg px-2 text-sm text-muted hover:text-ink"
      >
        <Icon name="back" size={16} />
        {found?.placeName ?? t('menu.backToPlace')}
      </a>
      {menu.isPending ? (
        <p role="status" className="text-muted">
          {t('menu.loading')}
        </p>
      ) : null}
      {menu.isError ? (
        <div role="alert" className="grid justify-items-start gap-3">
          <p>{t('menu.error')}</p>
          <Button onClick={() => void menu.refetch()}>{t('common.retry')}</Button>
        </div>
      ) : null}
      {menu.isSuccess && !found ? <PageHeader title={t('dbError.menu_not_found')} /> : null}
      {found ? <MenuView api={api} menu={found} canEdit={canEdit} /> : null}
    </>
  );
}

function MenuView({ api, menu, canEdit }: { api: ConsoleApi; menu: MenuDetail; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [source, setSource] = useState(menu);
  const [draft, setDraft] = useState(() => buildDraft(menu));
  const [invalid, setInvalid] = useState(false);
  const added = useRef(0);
  const focusKey = useRef<string | null>(null);
  // Fresh data (after saving, or a first reading): start again from what the database has.
  if (source !== menu) {
    const next = buildDraft(menu);
    // The first reading form goes away: focus moves to the first dish instead of the page.
    if (!source.first && menu.first) focusKey.current = next[0]?.key ?? null;
    setSource(menu);
    setDraft(next);
  }

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['menu', menu.id] }),
      queryClient.invalidateQueries({ queryKey: ['menus', menu.placeId] }),
    ]);
  const save = useMutation({
    mutationFn: ({ dishes, removed }: { dishes: DishInput[]; removed: string[] }) =>
      api.saveDishes(menu.id, dishes, removed),
    onSuccess: refresh,
  });

  function edit(key: string, patch: Partial<Pick<DraftDish, 'name' | 'price'>>) {
    save.reset(); // a "saved" message must never sit next to unsaved edits
    setInvalid(false);
    setDraft((rows) => rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  function addDish() {
    save.reset();
    added.current += 1;
    const key = `new-${added.current}`;
    focusKey.current = key;
    setDraft((rows) => [...rows, { key, section: null, name: '', price: '', row: null }]);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    // New dishes and changed ones go to the database; an emptied name removes a saved dish.
    const changed = draft.filter((dish) => {
      const saved = dish.row?.dish;
      if (!dish.name.trim()) return false;
      return !saved || dish.name.trim() !== saved.name || parsePrice(dish.price) !== saved.price;
    });
    const parsed = DishInputListSchema.safeParse(
      changed.map((dish) => ({
        id: dish.row?.dish?.id ?? null,
        updated_at: dish.row?.dish?.updatedAt ?? null,
        section: dish.section,
        name: dish.name,
        price: parsePrice(dish.price),
      })),
    );
    setInvalid(!parsed.success);
    if (!parsed.success) return;
    const removed = draft.flatMap((dish) =>
      dish.row?.dish && !dish.name.trim() ? [dish.row.dish.id] : [],
    );
    save.mutate({ dishes: parsed.data, removed });
  }

  const compared = menu.second?.readable === true;
  const read = draft.filter((dish) => dish.row?.first || dish.row?.second);
  const differences = read.filter((dish) => dish.row?.difference).length;
  const reviewed = read.filter((dish) => isReviewed(dish.row)).length;
  const summaryIcon: IconName = !compared
    ? 'pending'
    : differences > reviewed
      ? 'conflict'
      : 'check';
  const summary = [
    t(read.length === 1 ? 'menu.summary.dishes.one' : 'menu.summary.dishes', {
      count: read.length,
    }),
    ...(!compared
      ? [t('menu.summary.pending')]
      : differences === 0
        ? [t('menu.summary.same')]
        : [
            t('menu.summary.differences', { count: differences }),
            ...(reviewed > 0
              ? [
                  t(reviewed === 1 ? 'menu.summary.reviewed.one' : 'menu.summary.reviewed', {
                    count: reviewed,
                  }),
                ]
              : []),
          ]),
  ].join(' · ');

  return (
    <>
      <PageHeader
        title={t('menu.title', { place: menu.placeName })}
        subtitle={[
          menuLabel(menu),
          t(`menuStatus.${menu.status}`),
          menu.lastReadAt ? t('menus.lastRead', { date: formatDate(menu.lastReadAt) }) : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          menu.url ? (
            <a
              href={menu.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`${buttonBase} ${secondaryLook}`}
            >
              <Icon name="external" />
              {t('menu.open')}
              <span className="sr-only">{t('menu.newTab')}</span>
            </a>
          ) : null
        }
      />
      {menu.isLinkOnly ? <p className="text-muted">{t('menu.linkOnly')}</p> : null}
      {!menu.isLinkOnly && !menu.first && canEdit ? (
        <FirstReadingCard api={api} menuId={menu.id} onSaved={refresh} />
      ) : null}
      {!menu.isLinkOnly && draft.length === 0 && !canEdit ? (
        <p className="text-muted">{t('menu.empty')}</p>
      ) : null}

      {draft.length > 0 ? (
        <Card title={t('menu.dishes')}>
          <form className="grid gap-5" onSubmit={submit} noValidate>
            <div className="grid gap-1 text-sm">
              <p className="inline-flex items-center gap-1.5 font-medium">
                <Icon name={summaryIcon} size={16} />
                {summary}
              </p>
              {canEdit ? <p className="text-muted">{t('menu.removeHint')}</p> : null}
            </div>
            <div
              role="region"
              aria-labelledby="menu-dishes-caption"
              tabIndex={0}
              className="overflow-x-auto rounded-2xl border border-line"
            >
              <table
                role="table"
                className="w-full border-collapse text-left text-[14.5px] max-[639px]:block min-[640px]:min-w-[880px]"
              >
                <caption id="menu-dishes-caption" className="sr-only">
                  {t('menu.dishes')}
                </caption>
                <thead role="rowgroup" className="text-[13px] text-muted max-[639px]:sr-only">
                  <tr role="row">
                    {(['dish', 'first', 'second', 'compare', 'name', 'price'] as const).map(
                      (column) => (
                        <th
                          key={column}
                          role="columnheader"
                          scope="col"
                          className="border-b border-line px-3 py-3 font-semibold whitespace-nowrap first:pl-4 last:pr-4"
                        >
                          {t(`menu.column.${column}`)}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody role="rowgroup" className="max-[639px]:block">
                  {draft.map((dish, index) => {
                    const comparison = comparisonOf(dish.row, compared);
                    const reference =
                      dish.row?.first?.name ??
                      dish.row?.second?.name ??
                      dish.row?.dish?.name ??
                      t('menu.newDish', { n: index + 1 });
                    const badPrice =
                      invalid && dish.name.trim() !== '' && !priceIsValid(dish.price);
                    const strong = dish.row?.difference === 'price' && !isReviewed(dish.row);
                    return (
                      <tr
                        key={dish.key}
                        role="row"
                        className={`border-b border-line last:border-b-0 max-[639px]:grid max-[639px]:grid-cols-2 max-[639px]:gap-x-4 max-[639px]:gap-y-3 max-[639px]:p-4 ${
                          comparison.differs ? 'bg-canvas' : ''
                        }`}
                      >
                        <th
                          role="rowheader"
                          scope="row"
                          className="py-3.5 pr-3 pl-4 align-middle font-semibold max-[639px]:col-span-2 max-[639px]:p-0 min-[640px]:min-w-34"
                        >
                          {reference}
                          {dish.section ? (
                            <span className="block text-[13px] font-normal text-muted">
                              {dish.section}
                            </span>
                          ) : null}
                        </th>
                        <Cell label={t('menu.column.first')}>
                          <Reading reading={menu.first} item={dish.row?.first} strong={strong} />
                        </Cell>
                        <Cell label={t('menu.column.second')}>
                          <Reading reading={menu.second} item={dish.row?.second} strong={strong} />
                        </Cell>
                        <Cell label={t('menu.column.compare')} wide>
                          <span
                            className={`inline-flex items-center gap-1.5 min-[640px]:whitespace-nowrap ${
                              comparison.differs ? 'font-semibold' : ''
                            }`}
                          >
                            <Icon name={comparison.icon} size={16} />
                            {comparison.label}
                          </span>
                        </Cell>
                        <Cell label={t('menu.column.name')} wide>
                          {canEdit ? (
                            <input
                              ref={(element) => {
                                if (element && focusKey.current === dish.key) {
                                  focusKey.current = null;
                                  element.focus();
                                }
                              }}
                              aria-label={t('menu.nameLabel', { dish: reference })}
                              value={dish.name}
                              maxLength={160}
                              onChange={(event) => edit(dish.key, { name: event.target.value })}
                              className={`${controlClass} min-[640px]:min-w-40`}
                            />
                          ) : (
                            (dish.row?.dish?.name ?? (
                              <span className="text-muted">{t('menu.notInAntoja')}</span>
                            ))
                          )}
                        </Cell>
                        <Cell label={t('menu.column.price')} wide>
                          {canEdit ? (
                            <span className="relative block min-[640px]:w-36">
                              <span
                                aria-hidden="true"
                                className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-muted"
                              >
                                $
                              </span>
                              <input
                                aria-label={t('menu.priceLabel', { dish: reference })}
                                inputMode="numeric"
                                placeholder={t('menu.toConfirm')}
                                value={dish.price}
                                onChange={(event) => edit(dish.key, { price: event.target.value })}
                                aria-invalid={badPrice}
                                aria-describedby={badPrice ? 'menu-invalid' : undefined}
                                className={`${controlClass} pl-7`}
                              />
                            </span>
                          ) : dish.row?.dish ? (
                            <Price value={dish.row.dish.price} strong={false} />
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </Cell>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {canEdit ? (
              <FormFooter
                status={
                  <>
                    {invalid ? (
                      <Notice id="menu-invalid">{t('dbError.dish_invalid')}</Notice>
                    ) : null}
                    {save.isError ? <Notice>{errorText(save.error)}</Notice> : null}
                  </>
                }
                live={save.isSuccess ? t(save.data === 0 ? 'menu.unchanged' : 'menu.saved') : ''}
              >
                <Button onClick={addDish}>
                  <Icon name="plus" />
                  {t('menu.addDish')}
                </Button>
                <Button type="submit" variant="primary" disabled={save.isPending}>
                  {save.isPending ? t('place.saving') : t('menu.save')}
                </Button>
              </FormFooter>
            ) : null}
          </form>
        </Card>
      ) : null}
    </>
  );
}

/** A table cell; on phones each one shows its column name, and wide ones take the row. */
function Cell({
  label,
  wide = false,
  children,
}: {
  label: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <td
      role="cell"
      className={`px-3 py-3.5 align-middle max-[639px]:p-0 min-[640px]:last:pr-4 ${wide ? 'max-[639px]:col-span-2' : ''}`}
    >
      <span aria-hidden="true" className="mb-1 block text-[13px] text-muted min-[640px]:hidden">
        {label}
      </span>
      {children}
    </td>
  );
}

function Price({ value, strong }: { value: number | null; strong: boolean }) {
  return (
    <span className={strong ? 'font-semibold' : ''}>
      {value === null ? t('menu.noPrice') : formatPrice(value)}
    </span>
  );
}

/** What one reading says about the dish: its price, or why there is nothing to show. */
function Reading({
  reading,
  item,
  strong,
}: {
  reading: MenuReading | null;
  item: MenuReadItem | null | undefined;
  strong: boolean;
}) {
  if (!reading) return <span className="text-muted">{t('menu.pending')}</span>;
  if (!reading.readable) return <span className="text-muted">{t('menu.unreadable')}</span>;
  if (!item) return <span className="text-muted">{t('menu.notInReading')}</span>;
  return <Price value={item.price} strong={strong} />;
}

/** Until the automatic reading (C2.1), an editor pastes or types the first one. */
function FirstReadingCard({
  api,
  menuId,
  onSaved,
}: {
  api: ConsoleApi;
  menuId: string;
  onSaved: () => Promise<unknown>;
}) {
  const [text, setText] = useState('');
  const items = parseMenuText(text);
  const save = useMutation({
    mutationFn: () => api.saveFirstReading(menuId, items),
    onSuccess: onSaved,
  });
  return (
    <Card title={t('menu.first.title')}>
      <form
        className="grid gap-5"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Field
          label={t('menu.first.label')}
          htmlFor="menu-text"
          hint={t('menu.first.hint')}
          hintId="menu-text-hint"
        >
          <textarea
            id="menu-text"
            rows={8}
            value={text}
            onChange={(event) => {
              save.reset();
              setText(event.target.value);
            }}
            aria-describedby="menu-text-hint"
            className={`${controlClass} py-2.5`}
          />
        </Field>
        <FormFooter
          status={
            <>
              {save.isError ? <Notice>{errorText(save.error)}</Notice> : null}
              {text.trim() ? (
                <p className="text-muted">{t('menu.first.count', { count: items.length })}</p>
              ) : null}
            </>
          }
        >
          <Button type="submit" variant="primary" disabled={save.isPending || items.length === 0}>
            {save.isPending ? t('place.saving') : t('menu.first.save')}
          </Button>
        </FormFooter>
      </form>
    </Card>
  );
}
